import isEmpty from "lodash/isEmpty";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import { APIError } from "web-core/utils";

export function isValidationError(e: unknown) {
  if (e instanceof APIError) {
    return e.data?.code === "VALIDATION_ERROR";
  }
  return false;
}

export function getErrorMessage(
  error: unknown,
  fallback = "Something went wrong"
): string {
  if (error instanceof APIError) {
    const { message } = error;
    if (isValidationError(error)) {
      const { details } = error.data;
      if (details && typeof details === "object") {
        return Object.values(details).join("\n");
      }
      if (typeof details === "string") {
        return details;
      }
    }
    return message && typeof message === "object"
      ? JSON.stringify(message)
      : (message ?? fallback);
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

export function handleError(error: unknown, title: string) {
  if (error instanceof APIError) {
    let description = error.message;

    description =
      description && typeof description === "object"
        ? JSON.stringify(description)
        : description;

    toast.error(title, {
      description,
    });
  } else if (error instanceof Error) {
    toast.error(title, {
      description: error.message,
    });
  } else {
    toast.error(title);
  }
}

function validationDetailMessage(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return (value as unknown[])
      .map((v) => (typeof v === "string" ? v : JSON.stringify(v)))
      .join(", ");
  }
  if (value != null && typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value ?? "");
}

export function handleFormValidationErrors<T extends FieldValues>(
  form: UseFormReturn<T>,
  error: Error | unknown,
  prefix: string = "",
  /** Map API `details` keys to react-hook-form field names (e.g. phone_number → phone). */
  apiFieldMap?: Record<string, string>
) {
  if (!(error instanceof APIError)) return false;

  if (error.data?.code !== "VALIDATION_ERROR") return false;
  const { details } = error.data;
  if (isEmpty(details)) return false;
  Object.entries((details || {}) as Record<string, unknown>).forEach(
    ([key, value]) => {
      const mappedKey = apiFieldMap?.[key] ?? key;
      const path = (prefix ? `${prefix}.${mappedKey}` : mappedKey) as Path<T>;
      form.setError(path, {
        message: validationDetailMessage(value),
      });
    }
  );
  return true;
}
